/**
 * QA Create Modal
 * Modal for creating new question-answer artifacts
 */

import React, { useEffect, useState } from 'react';
import { advisorApiService } from '../../services/advisor-api';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/QADetailModal.css';

interface QACreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate?: () => void; // Callback after successful creation
}

export const QACreateModal: React.FC<QACreateModalProps> = ({ isOpen, onClose, onCreate }) => {
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [programCodes, setProgramCodes] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [visaRequirements, setVisaRequirements] = useState<string[]>([]);

  // Available options from backend
  const [availableProgramCodes, setAvailableProgramCodes] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availableVisaRequirements, setAvailableVisaRequirements] = useState<string[]>([]);

  // Check if user can create (user or admin role)
  const canCreate = user?.role === 'user' || user?.role === 'admin';

  // Reset form when modal closes or opens
  useEffect(() => {
    if (isOpen) {
      setQuestion('');
      setAnswer('');
      setProgramCodes([]);
      setStatuses([]);
      setVisaRequirements([]);
      setError(null);
      fetchAvailableOptions();
    }
  }, [isOpen]);

  const fetchAvailableOptions = async () => {
    try {
      const [programCodesRes, statusesRes, visaReqRes] = await Promise.all([
        advisorApiService.getProgramCodes(),
        advisorApiService.getStatuses(),
        advisorApiService.getVisaRequirements(),
      ]);

      if (programCodesRes.data) {
        setAvailableProgramCodes(programCodesRes.data.program_codes);
      }
      if (statusesRes.data) {
        setAvailableStatuses(statusesRes.data.statuses);
      }
      if (visaReqRes.data) {
        setAvailableVisaRequirements(visaReqRes.data.visa_requirements);
      }
    } catch (err) {
      console.error('Failed to fetch available options:', err);
      setError('Failed to load dropdown options');
    }
  };

  const handleSave = async () => {
    // Validate fields
    if (!question.trim()) {
      setError('Question is required');
      return;
    }
    if (!answer.trim()) {
      setError('Answer is required');
      return;
    }
    if (programCodes.length === 0) {
      setError('At least one program code is required');
      return;
    }
    if (statuses.length === 0) {
      setError('At least one status is required');
      return;
    }
    if (visaRequirements.length === 0) {
      setError('At least one visa requirement is required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await advisorApiService.createQAQuestion({
        question: question.trim(),
        answer: answer.trim(),
        program_codes: programCodes,
        statuses: statuses,
        visa_requirements: visaRequirements,
      });

      if (response.data) {
        // Call the parent callback to refresh the list
        if (onCreate) {
          onCreate();
        }

        // Close the modal immediately after successful creation
        onClose();
      } else {
        setError(response.error || 'Failed to create question');
      }
    } catch (err) {
      console.error('Failed to create question:', err);

      let errorMessage = 'Failed to create question';
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as {
          response?: { data?: { error?: string }; status?: number };
          message?: string;
        };

        if (axiosError.response?.status === 409) {
          errorMessage =
            'A question with the same question and answer already exists in the database';
        } else if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error;
        } else if (axiosError.message) {
          errorMessage = axiosError.message;
        }
      }

      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleArrayItem = (arr: string[], item: string, setter: (arr: string[]) => void) => {
    if (arr.includes(item)) {
      setter(arr.filter((i) => i !== item));
    } else {
      setter([...arr, item]);
    }
  };

  if (!isOpen) return null;

  // Check if Save button should be disabled
  const isSaveDisabled =
    isSaving ||
    !question.trim() ||
    !answer.trim() ||
    programCodes.length === 0 ||
    statuses.length === 0 ||
    visaRequirements.length === 0 ||
    !canCreate;

  return (
    <div className="qa-modal-overlay" onClick={onClose}>
      <div className="qa-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="qa-modal-header">
          <h2>Create New Question & Answer</h2>
          <button
            type="button"
            className="qa-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="qa-modal-body">
          {/* Error Message */}
          {error && <div className="qa-modal-error">{error}</div>}

          {/* Question Section */}
          <div className="qa-modal-section">
            <h3>Question</h3>
            <textarea
              className="qa-modal-textarea"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              placeholder="Enter question..."
            />
          </div>

          {/* Answer Section */}
          <div className="qa-modal-section">
            <h3>Answer</h3>
            <textarea
              className="qa-modal-textarea"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={6}
              placeholder="Enter answer..."
            />
          </div>

          {/* Program Codes Section */}
          <div className="qa-modal-section">
            <h3>Program Codes</h3>
            <div className="qa-modal-checkbox-group">
              {availableProgramCodes.map((code) => (
                <label key={code} className="qa-modal-checkbox-label">
                  <input
                    type="checkbox"
                    checked={programCodes.includes(code)}
                    onChange={() => toggleArrayItem(programCodes, code, setProgramCodes)}
                  />
                  <span>{code}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Visa Requirements Section */}
          <div className="qa-modal-section">
            <h3>Visa Requirements</h3>
            <div className="qa-modal-checkbox-group">
              {availableVisaRequirements.map((visa) => (
                <label key={visa} className="qa-modal-checkbox-label">
                  <input
                    type="checkbox"
                    checked={visaRequirements.includes(visa)}
                    onChange={() => toggleArrayItem(visaRequirements, visa, setVisaRequirements)}
                  />
                  <span>{visa}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status Section */}
          <div className="qa-modal-section">
            <h3>Status</h3>
            <div className="qa-modal-checkbox-group">
              {availableStatuses.map((status) => (
                <label key={status} className="qa-modal-checkbox-label">
                  <input
                    type="checkbox"
                    checked={statuses.includes(status)}
                    onChange={() => toggleArrayItem(statuses, status, setStatuses)}
                  />
                  <span>{status}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div className="qa-modal-footer">
          <button
            type="button"
            className="qa-modal-button qa-modal-button-primary"
            onClick={handleSave}
            disabled={isSaveDisabled}
            title={
              !canCreate
                ? `You need 'user' or 'admin' permission to create. Current role: ${user?.role || 'none'}`
                : ''
            }
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
